import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import * as CONSTANTS from '../../constants/login/login-remember-me-constant';
import {
  AuthAccessTokenDetails,
  ILoginUserNamePasswordInput,
  LoginAuthWithCredentialsResponse,
  LoginOAuthParams,
  LoginOAuthResponse,
  PkceCode,
  SwapAuthForAccessTokens
} from '../../models/login';
import { LoginPostWithContinueService } from '../../services/login/data-services/login-post-with-continue/login-post-with-continue-service';
import { LoginPostWithCredService } from '../../services/login/data-services/login-post-with-cred/login-post-with-cred-service';
import { LoginPostWithRememberUserService } from '../../services/login/data-services/login-post-with-remember-user/login-post-with-remember-user-service';
import { SwapAccessTokenService } from '../../services/login/data-services/swap-access-token/swap-access-token-service';
import { ChallengeScreenError } from '../../services/security-verification/challenge-success.service';
import { transformSwapAuthTokenDetails } from '../../utils/login/initiate-login-utils';
import { LoginRememberMeErrorUtilService } from '../../utils/login/login-remember-me-error-util.service';
import { LoginRememberMeUtilsService } from '../../utils/login/login-remember-me-utils.service';

type ErrorResponse =
  | LoginOAuthResponse
  | LoginAuthWithCredentialsResponse
  | SwapAuthForAccessTokens
  | HttpErrorResponse
  | ChallengeScreenError
  | string;

@Injectable({ providedIn: 'root' })
export class PostWithCredentialsFacadeHelperService {
  loginPostWithCred = inject(LoginPostWithCredService);
  loginPostWithContinue = inject(LoginPostWithContinueService);
  loginPostWithRememberUser = inject(LoginPostWithRememberUserService);
  loginRememberMeUtils = inject(LoginRememberMeUtilsService);
  LoginRememberMeErrorUtil = inject(LoginRememberMeErrorUtilService);
  swapAccessToken = inject(SwapAccessTokenService);
  injector = inject(Injector);

  flowId = '';
  encyToken = '';
  uid = '';
  pkceCode = {} as PkceCode;

  assignRememberMeDetails(response: LoginAuthWithCredentialsResponse): void {
    this.encyToken = response.encryptedToken ? response.encryptedToken : '';
    this.uid = response.fingerprint ? response.fingerprint : '';
  }

  private getResponseId(response: LoginAuthWithCredentialsResponse): string {
    return response.id || '';
  }

  handleCredentialsResponse(
    response: LoginAuthWithCredentialsResponse,
    loginOAuthParams: LoginOAuthParams,
    requestInput: ILoginUserNamePasswordInput
  ): Observable<LoginAuthWithCredentialsResponse> {
    if (response.status === CONSTANTS.SHOULD_REMEMBER_USER) {
      return this.loginPostWithRememberUser.loginPostWithRememberUser(
        loginOAuthParams.oauthConfig,
        this.getResponseId(response),
        requestInput
      );
    } else if (response.status === CONSTANTS.COMPLETED) {
      return of(response);
    } else {
      return throwError(response);
    }
  }

  handleRememberMeResponse(
    response: LoginAuthWithCredentialsResponse,
    loginOAuthParams: LoginOAuthParams
  ): Observable<LoginAuthWithCredentialsResponse> {
    if (response.status === CONSTANTS.COMPLETED) {
      return this.swapAccessToken.swapAuthCodeForToken(
        loginOAuthParams,
        response?.authorizeResponse?.code
      );
    } else if (response.status === CONSTANTS.REMEMBERED_DEVICE_DETAILS) {
      this.assignRememberMeDetails(response);
      return this.loginPostWithContinue.loginPostWithContinue(
        loginOAuthParams.oauthConfig,
        this.getResponseId(response)
      );
    } else {
      return throwError(response);
    }
  }

  processSwapResponse(
    response: LoginAuthWithCredentialsResponse | SwapAuthForAccessTokens,
    loginOAuthParams: LoginOAuthParams
  ): Observable<SwapAuthForAccessTokens> {
    if ('status' in response) {
      if (response.status === CONSTANTS.COMPLETED) {
        return this.swapAccessToken.swapAuthCodeForToken(
          loginOAuthParams,
          response?.authorizeResponse?.code
        );
      }
      return throwError(response);
    }
    return 'access_token' in response ? of(response) : throwError(response);
  }

  processTokenResponse(
    response: SwapAuthForAccessTokens,
    loginOAuthParams: LoginOAuthParams
  ): Observable<AuthAccessTokenDetails> {
    if ('access_token' in response) {
      this.loginRememberMeUtils.sendTokenServiceWorker(
        loginOAuthParams.oauthConfig,
        response
      );
      const authAccessTokenDetails = transformSwapAuthTokenDetails(response);
      return this.encyToken && this.uid
        ? of({
            ...authAccessTokenDetails,
            encyToken: this.encyToken,
            uid: this.uid
          })
        : of(authAccessTokenDetails);
    }
    return throwError(response);
  }

  isChallengeError(error: ErrorResponse): boolean {
    return (
      (error as { status?: number })?.status === 428 ||
      error instanceof ChallengeScreenError
    );
  }

  shouldRetryLogin(error: ErrorResponse): boolean {
    const errorObj = error as { error?: { code?: string } };
    return (
      errorObj?.error?.code !== CONSTANTS.VALIDATION_ERROR &&
      !this.isChallengeError(error)
    );
  }
}
